import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import {
	Formation,
	FormationService,
	FormationAdministrativeStatusValues,
} from '@app/models/formation';
import {
	FormationSession,
	FormationSessionService,
} from '@app/models/formation-session';
import { FormationInterventionWalkerService } from '@app/models/formation-intervention/formation-intervention-walker.service';
import { User } from '@app/models/user/user';
import { DocumentAdministrative } from '@app/models/document-administrative';
import { AttestationFinDeFormationService } from '@app/models/attestation-fin-de-formation';
import { HttpClient } from '@angular/common/http';
import { NzMessageService, NzModalService } from 'ng-zorro-antd';
import { ErrorService } from '@app/services/error.service';

import { PageInterface, PageService } from '@app/services/page.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AtomFormationTableAttestationFinFormationComponent } from '@app/atoms/formation/atom-formation-table-attestation-fin-formation/atom-formation-table-attestation-fin-formation.component';
import { FormationTheme } from '@app/models/formation-theme';

@Component({
	selector: 'app-molecule-formation-details-admin',
	templateUrl: './molecule-formation-details-admin.component.html',
	styleUrls: ['./molecule-formation-details-admin.component.less'],
})
export class MoleculeFormationDetailsAdminComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	@ViewChild('attestationFinFormationTable')
	attestationFinFormationTable: AtomFormationTableAttestationFinFormationComponent;

	protected skeleton: PageSkeletonType = 'list';
	private unsubscribe$ = new Subject<void>();
	formation: Formation;
	formationSessions: FormationSession[] = [];
	numberSessions: number = 0;
	formationEnd: number;

	sorts = ['created_at'];
	orders = ['asc', 'desc'];
	loading = false;
	attestationFinFormationLoading = false;

	statesAdmin = FormationAdministrativeStatusValues;

	// Respondants (students) for the formation
	respondants: User[] = [];

	attestationFinFormationTemplateOptions: string[] = [
		'attestation-fin-formation-fae-ach',
		'attestation-fin-formation-fae-ama',
		'attestation-fin-formation-fae-th-tsh',
		'attestation-fin-formation-ACH-V2-BL',
		'attestation-fin-formation-AMA-V2-BL',
		'attestation-fin-formation-TH-TSH-V2-BL',
	];

	defaultTemplate: string | null = null;

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		public formationInterventionWalkerService: FormationInterventionWalkerService,
		public formationService: FormationService,
		public formationSessionService: FormationSessionService,
		private route: ActivatedRoute,
		private attestationFinDeFormationService: AttestationFinDeFormationService,
		private errorService: ErrorService,
		private messageConfirmation: NzMessageService,
		private http: HttpClient,
		private modal: NzModalService
	) {
		super(pageService, translate);
	}

	async ngOnInit() {
		super.ngOnInit();
		this.loading = true;
		this.formation = await this.formationService.getAsAdmin(
			this.route.snapshot.params.id,
			{
				_populate: [
					'sponsor.avatar',
					'sessions.interventions.thematiques',
					'sessions.interventions.formateur',
					'respondants',
					'themes',
				],
			}
		);

		this.defaultTemplate = this.getDefaultTemplate(this.formation);
		this.refreshPageData();
		await this.getFormationSessions();
		await this.getRespondants();

		this.loading = false;
	}

	getDefaultTemplate(formation: Formation): string | null {
		if (formation.themesExists()) {
			const themes = formation.props.themes as FormationTheme[];
			if (themes[0]?.getLabel()) {
				return `attestation-fin-formation-${themes[0]
					?.getLabel()
					.toLowerCase()
					.replace(/ /g, '-')}`;
			}
		}
		return null;
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	async getFormationSessions() {
		this.loading = true;

		if (this.formation.sessionsExists()) {
			this.formationSessions = this.formation.props
				.sessions as FormationSession[];
			this.numberSessions = this.formationSessions.length;

			// Calculate the formation end date based on the interventions in all sessions
			// This might need to be adjusted based on your data structure
			this.formationEnd = this.formation.props.end_date as number;
		} else {
			this.formationSessions = [];
			this.numberSessions = 0;
			this.formationEnd = 0;
		}

		this.loading = false;
	}

	trackById(_: number, session: FormationSession): string {
		return session.getId();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant('page_title-formation_list_session', {
				formation: this.formation ? this.formation.getLabel() : '',
			}),
			breadcrumbs: [
				{
					label: this.translate.instant('top_bar-formation_list'),
					link: ['/formations/list'],
				},
				{ label: this.formation ? this.formation.getLabel() : null },
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}

	async getRespondants() {
		if (this.formation.respondantsExists()) {
			this.respondants = this.formation.props.respondants as User[];
		} else {
			this.respondants = [];
		}
	}

	// Methods for attestation fin de formation
	isGenerateDisable(): boolean {
		return false;
	}

	setGenerateLoading(isLoading: boolean): void {
		this.attestationFinFormationLoading = isLoading;
	}

	isSendDisable(documents: DocumentAdministrative[]): boolean {
		return this.isDocSigned(documents);
	}

	private isDocSigned(doc: DocumentAdministrative[]) {
		if (this.docExists(doc)) {
			return doc[0].props.status === 'signed';
		}
		return false;
	}

	private docExists(doc: DocumentAdministrative[]) {
		return doc.length > 0 && doc[0] !== null;
	}

	// Download a PDF
	downloadPdf(url: string, filename: string) {
		this.http
			.get(url, { responseType: 'blob' })
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((response) => {
				const file = new Blob([response], { type: response.type });

				const fileURL = URL.createObjectURL(file);
				const fakeDownload = document.createElement('a');
				fakeDownload.href = fileURL;
				fakeDownload.target = '_blank';
				fakeDownload.download = filename;
				document.body.appendChild(fakeDownload);
				fakeDownload.click();
				URL.revokeObjectURL(fileURL);
				fakeDownload.remove();
			});
	}

	// Attestation Fin de Formation methods
	async handleGenerate(event: {
		docType: string;
		template: string;
		totalTimeOverride?: number;
	}): Promise<void> {
		this.setGenerateLoading(true);
		try {
			// Generate for all respondants
			if (this.respondants && this.respondants.length > 0) {
				// Process respondants one by one
				for (const respondant of this.respondants) {
					await this.generateAttestationFinFormation(
						respondant.props._id,
						event.template,
						event.totalTimeOverride
					);
				}

				// Refresh the table
				await this.attestationFinFormationTable.refresh();
			}

			// Show success message
			this.messageConfirmation.success(
				this.translate.instant('common_success_generate')
			);
		} catch (error) {
			this.errorService.handle(error);
		} finally {
			this.setGenerateLoading(false);
		}
	}

	async handleGenerateForRespondant(event: {
		docType: string;
		respondantId: string;
		template: string;
		totalTimeOverride?: number;
	}): Promise<void> {
		this.setGenerateLoading(true);
		try {
			// Generate for specific respondant
			await this.generateAttestationFinFormation(
				event.respondantId,
				event.template,
				event.totalTimeOverride
			);

			// Refresh the table
			await this.attestationFinFormationTable.refresh();

			// Show success message
			this.messageConfirmation.success(
				this.translate.instant('common_success_generate')
			);
		} catch (error) {
			this.errorService.handle(error);
		} finally {
			this.setGenerateLoading(false);
		}
	}

	private async generateAttestationFinFormation(
		respondantId: string,
		template: string,
		totalTimeOverride?: number
	): Promise<void> {
		try {
			await this.formationService.generateAttestationFinFormation(
				this.formation.getId(),
				{
					stagiaire: respondantId,
					template: template,
					total_time_override: totalTimeOverride,
				}
			);
		} catch (error) {
			throw error;
		}
	}

	async handleReadDocumentAttestationFinFormation(id: string) {
		try {
			const { url } =
				await this.formationService.getPresignedAttestationFinFormationAsAdmin(
					this.formation.getId(),
					id
				);
			window.open(url, '_blank');
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleDownloadDocumentAttestationFinFormation(event: {
		attestationId: string;
		filename: string;
	}) {
		try {
			const { url } =
				await this.formationService.getPresignedAttestationFinFormationAsAdmin(
					this.formation.getId(),
					event.attestationId
				);

			this.downloadPdf(url, event.filename);
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleSendAttestationFinFormation(attestationId: string) {
		try {
			await this.formationService.sendEmailAttestationFinFormation(
				this.formation.getId(),
				attestationId
			);

			await this.attestationFinFormationTable.refresh();
			this.createMessageConfirmationSend();
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	async handleSendAllAttestationFinFormation(attestationIds: string[]) {
		if (!attestationIds || attestationIds.length === 0) {
			return;
		}

		try {
			// Process all attestations one by one
			let successCount = 0;
			let errorCount = 0;

			await Promise.all(
				attestationIds.map(async (attestationId) => {
					try {
						await this.formationService.sendEmailAttestationFinFormation(
							this.formation.getId(),
							attestationId
						);
						successCount++;
					} catch (error) {
						console.error(
							`Error sending email for attestation ${attestationId}:`,
							error
						);
						errorCount++;
					}
				})
			);

			// Refresh the table regardless of success/failure
			await this.attestationFinFormationTable.refresh();

			// Show appropriate message based on results
			if (errorCount === 0) {
				// All successful
				this.messageConfirmation.success(
					this.translate.instant('common_document_send_all_success', {
						count: successCount,
					})
				);
			} else if (successCount === 0) {
				// All failed
				this.messageConfirmation.error(
					this.translate.instant('common_document_send_all_error')
				);
				this.errorService.handle(
					new Error(`Failed to send ${errorCount} emails`)
				);
			} else {
				// Mixed results
				this.messageConfirmation.warning(
					this.translate.instant('common_document_send_all_partial', {
						success: successCount,
						error: errorCount,
					})
				);
			}
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	showDeleteConfirmOnAttestationFinFormation(idDocument: string): void {
		this.modal.confirm({
			nzTitle: this.translate.instant('document_confirm_delete'),
			nzContent: this.translate.instant(
				'document_confirm_delete_content'
			),
			nzOkText: this.translate.instant('common_yes'),
			nzOkType: 'danger',
			nzOnOk: async () => {
				try {
					// Delete the attestation fin de formation using the service
					await this.attestationFinDeFormationService.removeAsAdmin(
						idDocument
					);

					// Refresh the table to update the UI
					await this.attestationFinFormationTable.refresh();

					// Show success message
					this.messageConfirmation.success(
						this.translate.instant('common_success_delete')
					);
				} catch (error) {
					this.errorService.handle(error);
				}
			},
			nzCancelText: this.translate.instant('common_no'),
		});
	}

	createMessageConfirmationSend(): void {
		this.messageConfirmation.success(
			this.translate.instant('common_document_send')
		);
	}

	createMessageErrorSend(): void {
		this.messageConfirmation.error(
			this.translate.instant('send_error_confirm')
		);
	}

	// ====== BULK ACTIONS FOR ATTESTATION FIN FORMATION ======

	async handleBulkGenerateFinFormation(event: {
		selectedIds: string[];
		partialRespondantIds: string[];
		docType: string;
		template: string;
		totalTimeOverride?: number;
	}): Promise<void> {
		this.setGenerateLoading(true);

		try {
			const promises: Promise<void>[] = [];

			// Generate for selected existing attestations
			if (event.selectedIds && event.selectedIds.length > 0) {
				event.selectedIds.forEach((attestationId) => {
					// Find the respondant for this attestation
					const attestation =
						this.attestationFinFormationTable.attestations.find(
							(att) => att.props._id === attestationId
						);
					if (attestation && attestation.stagiaireExists()) {
						const stagiaire = attestation.props.stagiaire;
						if (stagiaire instanceof User) {
							promises.push(
								this.generateAttestationFinFormation(
									stagiaire.props._id,
									event.template,
									event.totalTimeOverride
								)
							);
						}
					}
				});
			}

			// Generate for partial respondants (those without attestations)
			if (
				event.partialRespondantIds &&
				event.partialRespondantIds.length > 0
			) {
				event.partialRespondantIds.forEach((respondantId) => {
					promises.push(
						this.generateAttestationFinFormation(
							respondantId,
							event.template,
							event.totalTimeOverride
						)
					);
				});
			}

			// If no selection, generate for all respondants
			if (
				(!event.selectedIds || event.selectedIds.length === 0) &&
				(!event.partialRespondantIds ||
					event.partialRespondantIds.length === 0)
			) {
				this.respondants.forEach((respondant) => {
					promises.push(
						this.generateAttestationFinFormation(
							respondant.props._id,
							event.template,
							event.totalTimeOverride
						)
					);
				});
			}

			// Execute all generation operations in parallel
			await Promise.all(promises);

			// Refresh the table to show new attestations
			await this.attestationFinFormationTable.refresh();
		} catch (error) {
			console.error('Error in bulk generate:', error);
			this.errorService.handle(error);
		} finally {
			this.setGenerateLoading(false);
		}
	}

	async handleBulkSendFinFormation(attestationIds: string[]): Promise<void> {
		if (!attestationIds || attestationIds.length === 0) return;

		try {
			const promises = attestationIds.map((id) =>
				this.handleSendAttestationFinFormation(id)
			);
			await Promise.all(promises);

			// Refresh the table to show updated status
			await this.attestationFinFormationTable.refresh();
		} catch (error) {
			console.error('Error in bulk send:', error);
			this.errorService.handle(error);
		}
	}

	async handleBulkDeleteFinFormation(
		attestationIds: string[]
	): Promise<void> {
		if (!attestationIds || attestationIds.length === 0) return;

		try {
			await this.executeBulkDeleteFinFormation(attestationIds);
		} catch (error) {
			console.error('Error in bulk delete:', error);
			this.errorService.handle(error);
		}
	}

	async handleBulkDownloadFinFormation(
		downloadItems: { attestationId: string; filename: string }[]
	): Promise<void> {
		if (!downloadItems || downloadItems.length === 0) return;

		try {
			// Process downloads in parallel
			await Promise.all(
				downloadItems.map((item) =>
					this.handleDownloadDocumentAttestationFinFormation(item)
				)
			);
		} catch (error) {
			console.error('Error in bulk download:', error);
			this.errorService.handle(error);
		}
	}

	private async executeBulkDeleteFinFormation(
		attestationIds: string[]
	): Promise<void> {
		const promises = attestationIds.map(async (attestationId) => {
			await this.attestationFinDeFormationService.removeAsAdmin(
				attestationId
			);
		});

		await Promise.all(promises);

		// Refresh the table to show updated data
		await this.attestationFinFormationTable.refresh();

		this.messageConfirmation.success(
			this.translate.instant('bulk_delete_success')
		);
	}
}
